import React from "react";
import {
  Box,
  Pagination,
  Select,
  MenuItem,
  Typography,
  FormControl,
  Divider,
} from "@mui/material";

/**
 * CustomPagination
 *
 * Props:
 * - page             (int)       current 1-indexed page
 * - totalPages       (int)       total number of pages
 * - total            (int)       total number of records
 * - pageSize         (int)       current page size
 * - onPageChange     (fn)        called with (newPage: int)
 * - onPageSizeChange (fn)        called with (newSize: int)
 * - pageSizeOptions  (int[])     defaults to [5, 10, 25, 50]
 * - sx               (object)    extra sx for the outer wrapper
 */
const CustomPagination = ({
  page = 1,
  totalPages = 1,
  total = 0,
  pageSize = 5,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 25, 50],
  sx = {},
}) => {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, total);

  return (
    <Box sx={{ ...sx }}>
      <Divider sx={{ mb: 0, borderColor: "divider" }} />

      <Box
        sx={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          flexWrap:       "wrap",
          gap:            1,
          px:             2,
          py:             1.25,
          bgcolor:        "transparent",
          borderTop:      "none",
          borderRadius:   "0 0 8px 8px",
        }}
      >
        {/* ── Left: record range + rows-per-page ─────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>

          {/* Record count */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", fontWeight: 500, fontSize: "0.8rem" }}
          >
            {total === 0
              ? "No records"
              : `Showing ${start}–${end} of ${total} records`}
          </Typography>

          {/* Rows-per-page */}
          {onPageSizeChange && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ whiteSpace: "nowrap", fontSize: "0.78rem", fontWeight: 500 }}
              >
                Rows per page:
              </Typography>

              <FormControl size="small" variant="outlined">
                <Select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  sx={{
                    fontSize:       "0.8rem",
                    fontWeight:     600,
                    height:         32,
                    minWidth:       64,
                    borderRadius:   1.5,
                    bgcolor:        "background.default",
                    "& .MuiSelect-select": {
                      py:  "5px",
                      px:  "10px",
                      pr:  "28px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    }
                  }}
                >
                  {pageSizeOptions.map((opt) => (
                    <MenuItem
                      key={opt}
                      value={opt}
                      sx={{ fontSize: "0.8rem", minHeight: 36, fontWeight: 500 }}
                    >
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {/* ── Right: page buttons ─────────────────────────────── */}
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => onPageChange && onPageChange(value)}
          color="primary"
          shape="rounded"
          size="small"
          siblingCount={1}
          boundaryCount={1}
          sx={{
            "& .MuiPaginationItem-root": {
              fontSize:   "0.8rem",
              minWidth:   32,
              height:     32,
              fontWeight: 600,
              borderRadius: 1.5,
              transition: "all 0.2s ease",
              color: "text.secondary",
              "&:hover": {
                bgcolor: "action.hover",
                color: "text.primary",
              },
            },
            "& .MuiPaginationItem-root.Mui-selected": {
              fontWeight: 700,
              backgroundColor: (theme) => theme.palette.primary.main + "1A", // 10% opacity hex
              color: "primary.main",
              "&:hover": {
                 backgroundColor: (theme) => theme.palette.primary.main + "26", // 15% opacity hex
              }
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default CustomPagination;